import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import Typography from 'carbon-react/lib/components/typography';
import Textbox from 'carbon-react/lib/components/textbox';
import Button from 'carbon-react/lib/components/button';
import Link from 'carbon-react/lib/components/link';

import {
  HostedUIIdentityProvider,
  PREDICT_PRODUCT_SLUG,
  ROUTE_PARAMS,
  interpolatePathParams,
  useWindowSize
} from '@futrli/shared-services';
import { AnyError } from '@futrli/errors';
import { IMAGE_URLS, UISystemIcon } from '@futrli/ui-components';

import styles from '../../core/common.scss';
import { DESKTOP_LAYOUT_LIMIT, FORM_FIELDS, LOGIN_FORM_ID, REGISTER_ROUTE } from '../../core/constants';
import { getFormValidator } from '../../utils/validation-utils';
import { FormattedMessage, useIntl } from 'react-intl';
import { HandleFederatedSignIn } from '../../federated-sign-in/use-federated-sign-in';
import { getErrorMessage } from '../../utils/login';

type LoginErrorData = Record<string, ReactNode>;

export interface Props {
  federatedSignInError?: AnyError;
  isLoading: boolean;
  onFederatedSignIn: HandleFederatedSignIn;
  onSubmit: (email: string) => Promise<void>;
}

export const SageIdLoginForm = ({ federatedSignInError, isLoading, onFederatedSignIn, onSubmit }: Props) => {
  const [emailTouched, setEmailTouched] = useState(false);
  const [email, setEmail] = useState('');
  const { width: windowWidth } = useWindowSize();

  const navigate = useNavigate();

  const intl = useIntl();

  useEffect(() => {
    if (typeof windowWidth === 'number' && windowWidth >= DESKTOP_LAYOUT_LIMIT) {
      document.body.classList.add(styles.bodyStyles);
    }

    return () => {
      if (typeof windowWidth === 'number' && windowWidth >= DESKTOP_LAYOUT_LIMIT) {
        document.body.classList.remove(styles.bodyStyles);
      }
    };
  }, [windowWidth]);

  const validate = getFormValidator([FORM_FIELDS.username], LOGIN_FORM_ID, intl);

  const [errors, setErrors] = useState<LoginErrorData>(
    validate(({ [FORM_FIELDS.username]: email } as unknown) as Record<string, string>)
  );

  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newEmail = e.target.value;
      setEmail(newEmail);
      setErrors(validate({ [FORM_FIELDS.username]: newEmail }));
      setEmailTouched(true);
    },
    [validate]
  );

  const handleBlur = useCallback(() => {
    setEmailTouched(true);
  }, []);

  const handleRegisterClick = useCallback(
    () => navigate(interpolatePathParams(REGISTER_ROUTE, { [ROUTE_PARAMS.PRODUCT_SLUG]: PREDICT_PRODUCT_SLUG })),
    [navigate]
  );

  const handleFederatedSignIn = useCallback(
    (customProvider: keyof typeof HostedUIIdentityProvider) => {
      return (event: React.MouseEvent) => {
        event.preventDefault();

        onFederatedSignIn(customProvider);
      };
    },
    [onFederatedSignIn]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const disabled = isLoading || Object.keys(errors).length > 0;
      if (e.key === 'Enter' && !disabled) {
        e.preventDefault();
        onSubmit(email);
      }
    },
    [email, errors, isLoading, onSubmit]
  );

  const handleFormSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const errors = validate({ [FORM_FIELDS.username]: email });
      if (Object.keys(errors).length === 0) {
        onSubmit(email);
      }
    },
    [email, onSubmit, validate]
  );

  const handleSubmit = useCallback(() => {
    onSubmit(email);
  }, [email, onSubmit]);

  const dataError = Object.keys(errors).length > 0;

  return (
    <main className={styles.newPageContainer}>
      <div className={classNames(styles.newPageContent, styles.mobileHeight)}>
        <div className={styles.registerFormContainer}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
            <UISystemIcon iconName="dark-by-sage-logo" width={202} height={90} />
          </div>
          <Typography variant="h1" textAlign="center" marginBottom={16}>
            <FormattedMessage id="login.log_in_to_Futrli" defaultMessage="Log in to Futrli" />
          </Typography>
          <Typography fontSize="16px" textAlign="center" marginBottom={40}>
            <FormattedMessage
              id="login.save_time_and_bookmark_this_page_for_next_time"
              defaultMessage="Save time and bookmark this page for next time."
            />
          </Typography>

          <form onSubmit={handleFormSubmit} noValidate>
            <Textbox
              id="email"
              label={intl.formatMessage({ id: 'common.email_address', defaultMessage: 'Email address' })}
              placeholder={intl.formatMessage({
                id: 'common.enter_email_address',
                defaultMessage: 'Enter your email address'
              })}
              value={email}
              onChange={handleEmailChange}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              name={FORM_FIELDS.username}
              size="large"
              disabled={isLoading}
              error={!!errors.username && emailTouched ? (errors.username as string) : ''}
            />
            {getErrorMessage(null, federatedSignInError)}
            <Button
              fullWidth
              disabled={isLoading || dataError}
              mb={16}
              mt={24}
              onClick={handleSubmit}
              buttonType="primary"
              size="large"
            >
              <FormattedMessage id="common.continue" defaultMessage="Continue" />
            </Button>
          </form>
          <Typography fontSize="16px" textAlign="center" marginBottom={16}>
            <FormattedMessage id="common.or" defaultMessage="or" />
          </Typography>
          <Button
            buttonType="secondary"
            className={styles.newXeroButtonSageCarbon}
            fullWidth
            disabled={isLoading}
            onClick={handleFederatedSignIn(HostedUIIdentityProvider.Xero)}
          >
            <Typography fontWeight="500" mb={0}>
              <img src={IMAGE_URLS.XERO_LOGO} alt="xero-logo" />
              <FormattedMessage id="login.sign_in_with_xero" defaultMessage="Sign in with Xero" />
            </Typography>
          </Button>
          <div style={{ marginTop: 40, display: 'flex' }}>
            <svg width="100%" height="1" viewBox="0 0 400 1" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 0.5H400" stroke="#7F7F7F" strokeWidth="0.5" />
            </svg>
          </div>
          <Typography fontSize="16px" mt={16}>
            <FormattedMessage id="login.new_user" defaultMessage="New user?" />{' '}
            <Link onClick={handleRegisterClick} className={styles.sageCarbonAuthLink}>
              <FormattedMessage id="login.create_account" defaultMessage="Create account" />
            </Link>
          </Typography>
        </div>
      </div>
    </main>
  );
};
